// PWA install-prompt capture + platform helpers.
//
// `beforeinstallprompt` can fire before React mounts, so this module registers
// its listener at import time (index.js imports it early) and stashes the event
// in a module singleton that the useInstallPrompt hook subscribes to.

let deferredPrompt = null;
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());
let registrationPromise = null;
let updateListener = null;
let updateReloadPending = false;
const watchedRegistrations = new WeakSet();
const watchedWorkers = new WeakSet();

const watchInstallingWorker = (registration, worker) => {
  if (!worker || watchedWorkers.has(worker)) return;
  watchedWorkers.add(worker);
  const reportInstalledUpdate = () => {
    if (worker.state === "installed" && navigator.serviceWorker.controller) {
      updateListener?.(registration);
    }
  };
  worker.addEventListener("statechange", reportInstalledUpdate);
  reportInstalledUpdate();
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    emit();
  });
}

export const getDeferredPrompt = () => deferredPrompt;

export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const registerServiceWorker = ({ onUpdate } = {}) => {
  if (onUpdate) updateListener = onUpdate;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return Promise.resolve(null);
  }

  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration) => {
        if (!watchedRegistrations.has(registration)) {
          watchedRegistrations.add(registration);
          registration.addEventListener("updatefound", () => {
            watchInstallingWorker(registration, registration.installing);
          });
          if (registration.waiting && navigator.serviceWorker.controller) {
            updateListener?.(registration);
          }
          watchInstallingWorker(registration, registration.installing);
          registration.update().catch(() => {});
        }
        return registration;
      })
      .catch(() => null);
  }

  return registrationPromise;
};

export const applyUpdate = (registration, reloadPage = () => window.location.reload()) => {
  if (
    updateReloadPending ||
    (!registration?.waiting && !registration?.installing) ||
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator)
  ) {
    return;
  }

  const requestActivation = (worker) => {
    if (!worker || updateReloadPending) return;
    updateReloadPending = true;
    let reloaded = false;
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      () => {
        if (reloaded) return;
        reloaded = true;
        reloadPage();
      },
      { once: true },
    );
    worker.postMessage({ type: "SKIP_WAITING" });
  };

  if (registration.waiting) {
    requestActivation(registration.waiting);
    return;
  }

  const worker = registration.installing;
  const requestActivationWhenInstalled = () => {
    if (worker.state === "installed") {
      requestActivation(registration.waiting || worker);
    }
  };
  worker.addEventListener("statechange", requestActivationWhenInstalled);
  requestActivationWhenInstalled();
};

// Trigger the native Android/desktop-Chrome install dialog. Returns the outcome
// ("accepted" | "dismissed") or null when no prompt is available.
export const promptInstall = async () => {
  if (!deferredPrompt) return null;
  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  emit();
  return outcome;
};

export const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

export const isIOS = () =>
  typeof window !== "undefined" &&
  /iphone|ipad|ipod/i.test(window.navigator.userAgent);
